import { ref } from 'vue';
import { defineStore } from 'pinia';
import type { GetItemsOptions } from '@/common/api';
import { getTags } from '@/common/api/getTags';
import { handleThrownError } from '@/common/errors/handleThrownError';
import type { Tag, TagPayload } from '@/types';

export const useTagsStore = defineStore('tagsStore', () => {
  const tags = ref<Tag[]>([]);
  const isLoading = ref(false);
  const hasLoadedOnce = ref(false);
  const isUnchanged = ref(false);
  const error = ref<Error | null>(null);

  function transformTagsData(data: TagPayload[]): Tag[] {
    return data.map(({ name, shortname, count }) => ({
      name,
      shortname,
      relatedItemsCount: count,
    }));
  }

  async function loadAllTags(options: GetItemsOptions = {}) {
    isLoading.value = true;
    isUnchanged.value = false;
    error.value = null;

    try {
      const { status, data } = await getTags(options);
      if (status !== 200 || !data) {
        throw new Error('Failed to load tags');
      }
      const newTags = transformTagsData(data);
      isUnchanged.value = newTags.length === tags.value.length;
      tags.value = newTags;
      hasLoadedOnce.value = true;
    } catch (err) {
      error.value = err as Error;
      handleThrownError({
        error: err,
        toastMessage: 'Something went wrong while loading tags.',
        log: true,
      });
    } finally {
      isLoading.value = false;
    }
  }

  return {
    tags,
    isLoading,
    hasLoadedOnce,
    isUnchanged,
    error,
    loadAllTags,
  };
});
